import React, { useState, useMemo } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { usePickHistory } from '../hooks/usePickHistory';
import ProfileSubNav from '../components/ProfileSubNav';
import './ProfilePage.css';

const STATS_FILTERS = [
  { key: 'all', label: 'All Time' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
];

export default function StatsPage() {
  const { userProfile } = useAuthContext();
  const { picks: pickHistory } = usePickHistory();
  const [statsFilter, setStatsFilter] = useState('all');

  const filteredStats = useMemo(() => {
    // For "All Time", use the denormalized profile values
    if (statsFilter === 'all') {
      const wins = userProfile?.totalCorrect || 0;
      const total = userProfile?.totalPicks || 0;
      const losses = total - wins;
      const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;
      return {
        currentStreak: userProfile?.currentStreak || 0,
        longestStreak: userProfile?.longestStreak || 0,
        record: `${wins}-${losses < 0 ? 0 : losses}`,
        winPct: `${winPct}%`,
      };
    }

    // Calculate cutoff date based on filter
    const now = new Date();
    let cutoff;
    if (statsFilter === 'week') {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
    } else if (statsFilter === 'month') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      cutoff = new Date(now.getFullYear(), 0, 1);
    }
    const cutoffMs = cutoff.getTime();

    const filtered = pickHistory.filter((p) => {
      if (p.status !== 'won' && p.status !== 'lost' && p.status !== 'push') return false;
      const ts = p.resolvedAt || p.createdAt;
      if (!ts) return false;
      const ms = typeof ts.toMillis === 'function' ? ts.toMillis()
        : typeof ts.seconds === 'number' ? ts.seconds * 1000
        : new Date(ts).getTime();
      return ms >= cutoffMs;
    });

    const wins = filtered.filter((p) => p.status === 'won').length;
    const losses = filtered.filter((p) => p.status === 'lost').length;
    const total = wins + losses;
    const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;

    const sorted = [...filtered].sort((a, b) => {
      const aTs = a.resolvedAt || a.createdAt;
      const bTs = b.resolvedAt || b.createdAt;
      const aMs = typeof aTs.toMillis === 'function' ? aTs.toMillis()
        : typeof aTs.seconds === 'number' ? aTs.seconds * 1000 : new Date(aTs).getTime();
      const bMs = typeof bTs.toMillis === 'function' ? bTs.toMillis()
        : typeof bTs.seconds === 'number' ? bTs.seconds * 1000 : new Date(bTs).getTime();
      return aMs - bMs;
    });

    let currentStreak = 0;
    let longestStreak = 0;
    for (const p of sorted) {
      if (p.status === 'won') {
        currentStreak++;
        if (currentStreak > longestStreak) longestStreak = currentStreak;
      } else if (p.status === 'lost') {
        currentStreak = 0;
      }
    }

    return {
      currentStreak,
      longestStreak,
      record: `${wins}-${losses}`,
      winPct: `${winPct}%`,
    };
  }, [statsFilter, pickHistory, userProfile]);

  if (!userProfile) {
    return <div className="profile-page"><p>Loading profile...</p></div>;
  }

  return (
    <div className="profile-page">
      <h1>Stats</h1>
      <ProfileSubNav />

      <div className="stats-card">
        <div className="stats-filters">
          {STATS_FILTERS.map((f) => (
            <button
              key={f.key}
              className={`stats-filter-btn ${statsFilter === f.key ? 'active' : ''}`}
              onClick={() => setStatsFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-value">{filteredStats.currentStreak}</span>
            <span className="stat-label">Current Streak</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{filteredStats.longestStreak}</span>
            <span className="stat-label">Longest Streak</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{filteredStats.record}</span>
            <span className="stat-label">W-L Record</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{filteredStats.winPct}</span>
            <span className="stat-label">Win %</span>
          </div>
        </div>
      </div>
    </div>
  );
}
